import React from 'react';

import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import PublishedWithChangesOutlinedIcon from '@mui/icons-material/PublishedWithChangesOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import AlarmOutlinedIcon from '@mui/icons-material/AlarmOutlined';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import AttachEmailOutlinedIcon from '@mui/icons-material/AttachEmailOutlined';
import {AutomationNode, NodeType} from "crqlar-react-common/dist/models/automation/automation";
import {PlayArrowRounded} from "@mui/icons-material";
import SyncIcon from '@mui/icons-material/Sync';
import {DEFAULT_RESERVATION_SOURCES} from './reservationTriggers';
import {DEFAULT_DAYS_AFTER_SENT} from './surveyTriggers';
import {DEFAULT_REPORT_FORMATS, DEFAULT_REPORT_PERIOD} from './surveyReport';

export interface NodeCategoryConfig {
    type: NodeType;
    label: string;
    icon: React.ReactElement;
}

export interface NodeCategory {
    name: string;
    nodes: NodeCategoryConfig[];
}

export const nodeCategories: NodeCategory[] = [
    {
        name: 'Triggers',
        nodes: [
            {type: NodeType.MANUAL_TRIGGER, label: 'Manual Trigger', icon: <PlayArrowRounded />},
            {type: NodeType.SCHEDULED_TRIGGER, label: 'Scheduled Trigger', icon: <ScheduleOutlinedIcon/>},
            {type: NodeType.FORM_SUBMITTED, label: 'Form Submitted', icon: <AssignmentOutlinedIcon/>},
            {type: NodeType.RESERVATION_CREATED, label: 'Reservation Created', icon: <EventAvailableOutlinedIcon/>},
            {
                type: NodeType.RESERVATION_STATUS_CHANGED,
                label: 'Reservation Status Changed',
                icon: <PublishedWithChangesOutlinedIcon/>
            },
            {type: NodeType.SURVEY_SUBMITTED, label: 'Survey Submitted', icon: <RateReviewOutlinedIcon/>},
            {type: NodeType.SURVEY_REMINDER_DUE, label: 'Survey Reminder Due', icon: <AlarmOutlinedIcon/>},
        ]
    },
    {
        name: 'Actions',
        nodes: [
            {type: NodeType.AUDIENCE, label: 'Audience', icon: <GroupOutlinedIcon/>},
            {type: NodeType.TEAM_MEMBERS, label: 'Team members', icon: <BadgeOutlinedIcon/>},
            {type: NodeType.SEND_EMAIL, label: 'Send Email', icon: <EmailOutlinedIcon/>},
            {type: NodeType.SEND_PUSH, label: 'Send Push Notification', icon: <NotificationsOutlinedIcon/>},
            {type: NodeType.SURVEY_INVITE, label: 'Send Survey', icon: <ForwardToInboxOutlinedIcon/>},
            {type: NodeType.SURVEY_REPORT, label: 'Survey report', icon: <AssessmentOutlinedIcon/>},
            {type: NodeType.SEND_TEAM_EMAIL, label: 'Send team email', icon: <AttachEmailOutlinedIcon/>},
            {type: NodeType.FULL_SYNC_AUDIENCE, label: 'Full Audience Sync', icon: <SyncIcon/>},
        ]
    },
    {
        name: 'Flow Control',
        nodes: [
            {type: NodeType.CONTINUE_IF, label: 'Continue if', icon: <FilterAltOutlinedIcon/>},
            {type: NodeType.IF_CONDITION, label: 'If Condition', icon: <RouteOutlinedIcon/>},
            {type: NodeType.WAIT, label: 'Wait', icon: <TimerOutlinedIcon/>},
            // Waits for every member run of the fan-out above, then continues once — flow control, not an ending.
            {type: NodeType.AFTER_EVERYONE, label: 'Wait for everyone', icon: <DoneAllOutlinedIcon/>},
        ]
    },
    {
        name: 'End',
        nodes: [
            {type: NodeType.FINISH, label: 'Stop', icon: <StopCircleOutlinedIcon/>},
        ]
    }
];

/** The label for a stored node of a retired type this build no longer knows. */
export const UNKNOWN_NODE_LABEL = 'Unknown step';

const UNKNOWN_NODE_COLOR = '#9ca3af';

/** The only list of triggers — import it (or isTriggerNode) instead of keeping a copy. */
export const triggerNodeTypes = [
    NodeType.MANUAL_TRIGGER,
    NodeType.SCHEDULED_TRIGGER,
    NodeType.FORM_SUBMITTED,
    NodeType.RESERVATION_CREATED,
    NodeType.RESERVATION_STATUS_CHANGED,
    NodeType.SURVEY_SUBMITTED,
    NodeType.SURVEY_REMINDER_DUE,
];

/** Triggers about an entity; a manual run of such a flow can pass one as the API's `entityId`. */
export const eventTriggerNodeTypes = triggerNodeTypes.filter(
    nodeType => nodeType !== NodeType.MANUAL_TRIGGER && nodeType !== NodeType.SCHEDULED_TRIGGER
);

export const isEventTriggerNode = (nodeType: NodeType): boolean => eventTriggerNodeTypes.includes(nodeType);

export const flowControlNodeTypes = [
    NodeType.IF_CONDITION,
    NodeType.CONTINUE_IF,
    NodeType.WAIT,
    NodeType.AFTER_EVERYONE,
];

export const endNodeTypes = [
    NodeType.FINISH,
];

/**
 * No source handle, no append button; the engine follows nothing. Stop only — Wait for everyone
 * keeps its output (AUTOMATIONS_UI.md, Decisions).
 */
export const terminalNodeTypes = [NodeType.FINISH];

export const isEndNode = (nodeType: NodeType): boolean => endNodeTypes.includes(nodeType);

export const isTerminalNode = (nodeType: NodeType): boolean => terminalNodeTypes.includes(nodeType);

/** One child run per member: everything after such a node runs once per member, not once per flow. */
export const fanOutNodeTypes = [
    NodeType.AUDIENCE,
    NodeType.TEAM_MEMBERS,
];

export const isFanOutNode = (nodeType: NodeType): boolean => fanOutNodeTypes.includes(nodeType);

export const getNodeConfig = (nodeType: NodeType): NodeCategoryConfig | null => {
    for (const category of nodeCategories) {
        const node = category.nodes.find(n => n.type === nodeType);
        if (node) return node;
    }
    return null;
};

/** Draw every node through here: an unknown (retired) type gets a neutral entry instead of a blank card. */
export const getNodeDisplay = (nodeType: NodeType): NodeCategoryConfig => (
    getNodeConfig(nodeType) ?? {
        type: nodeType,
        label: UNKNOWN_NODE_LABEL,
        icon: <HelpOutlineOutlinedIcon/>
    }
);

export const isKnownNodeType = (nodeType: NodeType): boolean => !!getNodeConfig(nodeType);

/** Name every step through here — references bind by this name, so writers and readers must agree. */
export const stepName = (node: Pick<AutomationNode, 'label' | 'type'>): string =>
    node.label || getNodeDisplay(node.type).label;

export const isTriggerNode = (nodeType: NodeType): boolean => {
    return triggerNodeTypes.includes(nodeType);
};

export const isFlowControlNode = (nodeType: NodeType): boolean => {
    return flowControlNodeTypes.includes(nodeType);
};

export const getNodeColor = (nodeType: NodeType): string => {
    if (isTriggerNode(nodeType)) return '#10b981';
    if (isFlowControlNode(nodeType)) return '#f59e0b';
    if (isEndNode(nodeType)) return '#8b5cf6';
    if (!isKnownNodeType(nodeType)) return UNKNOWN_NODE_COLOR;
    return '#3b82f6';
};

/**
 * Config for new nodes only (addNode, the agent's draftOperations, nodeDefinitions defaults).
 * Stored nodes are never touched: one without a `sources` key still means "all sources".
 */
export const getDefaultNodeConfig = (nodeType: NodeType): Record<string, any> => {
    switch (nodeType) {
        case NodeType.RESERVATION_CREATED:
        case NodeType.RESERVATION_STATUS_CHANGED:
            return {sources: [...DEFAULT_RESERVATION_SOURCES]};
        case NodeType.SURVEY_REMINDER_DUE:
            return {daysAfterSent: DEFAULT_DAYS_AFTER_SENT};
        case NodeType.AUDIENCE:
            return {marketingConsentOnly: false};
        case NodeType.SEND_TEAM_EMAIL:
            return {emailMode: 'BUILDER', attachmentSourceNodeIds: []};
        case NodeType.SURVEY_REPORT:
            return {period: DEFAULT_REPORT_PERIOD, formats: [...DEFAULT_REPORT_FORMATS]};
        default:
            // Team members too: nobody is picked until the user picks (teamMembers.ts).
            return {};
    }
};

/** No triggers in the pickers that attach to a step (AUTOMATIONS_UI.md, Decisions). */
export const stepNodeCategories: NodeCategory[] = nodeCategories
    .map(category => ({...category, nodes: category.nodes.filter(node => !isTriggerNode(node.type))}))
    .filter(category => category.nodes.length > 0);

/** The splice picker: a terminal step cannot sit in the middle of a connection, it has no output. */
export const insertNodeCategories: NodeCategory[] = stepNodeCategories
    .map(category => ({...category, nodes: category.nodes.filter(node => !isTerminalNode(node.type))}))
    .filter(category => category.nodes.length > 0);
